import { fetchAPI } from '../../../services/apiClient';

// ========================
// Types
// ========================

export interface CompraRecargaInput {
  vendedor_id?: string;
  fecha?: string;
  monto_compra: string;
  proveedor: string;
}

export interface CompraRecargaDetalle {
  id: string;
  monto_compra: string;
  proveedor: string;
  comision_porcentaje: string;
  comision_monto: string;
  vale: string;
  created_at: string | null;
}

export interface SaldoRecargaInfo {
  existe: boolean;
  fecha: string;
  vendedor_id: string;
  saldo_inicial: string;
  total_compras: string;
  total_comisiones: string;
  saldo_final: string;
  comision_porcentaje: string;
  compras: CompraRecargaDetalle[];
}

export interface SaldoHistorico {
  id: string;
  fecha: string;
  saldo_inicial: string;
  total_compras: string;
  total_comisiones: string;
  saldo_final: string;
  comision_porcentaje: string;
  created_at: string | null;
}

export interface SaldoDetalle extends SaldoHistorico {
  vendedor_id: string;
  local_id: string;
  compras: CompraRecargaDetalle[];
}

export interface CompraResult {
  ok: boolean;
  compra: {
    id: string;
    monto_compra: string;
    proveedor: string;
    comision_porcentaje: string;
    comision_monto: string;
    vale: string;
  };
  saldo: {
    id: string;
    fecha: string;
    saldo_inicial: string;
    total_compras: string;
    total_comisiones: string;
    saldo_final: string;
  };
}

export const PROVEEDORES_RECARGA = ['KOLBI', 'CLARO', 'LIBERTY'] as const;
export type ProveedorRecarga = typeof PROVEEDORES_RECARGA[number];

export interface ComisionConfig {
  local_id: string;
  local_nombre: string;
  proveedores: string[];
  comisiones: Record<string, string>;  // { KOLBI: "6.00", CLARO: "5.00", LIBERTY: "4.00" }
}

// ========================
// API calls
// ========================

export async function registrarCompraRecarga(
  data: CompraRecargaInput
): Promise<CompraResult> {
  return fetchAPI<CompraResult>('/recargas/compra', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function getSaldoRecarga(
  vendedorId?: string,
  fecha?: string
): Promise<SaldoRecargaInfo> {
  let url = '/recargas/saldo';
  const params: string[] = [];
  if (vendedorId) params.push(`vendedor_id=${vendedorId}`);
  if (fecha) params.push(`fecha=${fecha}`);
  if (params.length) url += '?' + params.join('&');
  return fetchAPI<SaldoRecargaInfo>(url);
}

export async function getHistorialSaldos(
  vendedorId?: string,
  limit: number = 30
): Promise<SaldoHistorico[]> {
  let url = `/recargas/historial?limit=${limit}`;
  if (vendedorId) url += `&vendedor_id=${vendedorId}`;
  return fetchAPI<SaldoHistorico[]>(url);
}

export async function getDetalleSaldo(
  saldoId: string
): Promise<SaldoDetalle> {
  return fetchAPI<SaldoDetalle>(`/recargas/saldo/${saldoId}`);
}

export async function configurarComision(
  localId: string,
  comisiones: Record<string, string>
): Promise<{ ok: boolean; local_id: string; comisiones: Record<string, { anterior: string; nueva: string }> }> {
  return fetchAPI('/recargas/config/comision/' + localId, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ comisiones }),
  });
}

export async function getComision(
  localId: string
): Promise<ComisionConfig> {
  return fetchAPI<ComisionConfig>(`/recargas/config/comision/${localId}`);
}

export async function establecerSaldoInicial(
  vendedorId: string,
  saldoInicial: string,
  fecha?: string
): Promise<{ ok: boolean; saldo: { id: string; fecha: string; saldo_inicial: string; total_compras: string; total_comisiones: string; saldo_final: string } }> {
  const body: Record<string, string> = {
    vendedor_id: vendedorId,
    saldo_inicial: saldoInicial,
  };
  if (fecha) body.fecha = fecha;
  return fetchAPI('/recargas/saldo-inicial', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}
